/**
 * ORBIS Admin - Router Configuration
 */

import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
      meta: { guest: true },
    },
    {
      path: '/',
      component: () => import('@/views/LayoutView.vue'),
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          name: 'dashboard',
          component: () => import('@/views/DashboardView.vue'),
        },
        {
          path: 'equipment',
          name: 'equipment-list',
          component: () => import('@/views/equipment/EquipmentListView.vue'),
        },
        {
          path: 'equipment/new',
          name: 'equipment-create',
          component: () => import('@/views/equipment/EquipmentFormView.vue'),
        },
        {
          path: 'equipment/:id',
          name: 'equipment-detail',
          component: () => import('@/views/equipment/EquipmentDetailView.vue'),
          props: true,
        },
        {
          path: 'equipment/:id/edit',
          name: 'equipment-edit',
          component: () => import('@/views/equipment/EquipmentFormView.vue'),
          props: true,
        },
        {
          path: 'equipment/:id/documents',
          name: 'equipment-documents',
          component: () => import('@/views/equipment/DocumentManagerView.vue'),
          props: true,
        },
        {
          path: 'equipment/:id/documents/:type/:confidential/history',
          name: 'equipment-document-history',
          component: () => import('@/views/equipment/DocumentHistoryView.vue'),
          props: true,
        },
        // v2.8: Quick Upload Wizard (US-12.1)
        {
          path: 'equipment/quick-upload',
          name: 'quick-upload',
          component: () => import('@/views/quick-upload/QuickUploadWizard.vue'),
        },
        // ===========================================
        // V3 Routes - Document Review Queue (Legacy)
        // ===========================================
        {
          path: 'documents/review-queue',
          name: 'document-review-queue',
          component: () => import('@/views/documents/DocumentReviewQueueView.vue'),
          meta: { requiresReviewer: true },
        },
        // v2.12: Document Detail View
        {
          path: 'documents/:id',
          name: 'document-detail',
          component: () => import('@/views/documents/DocumentDetailView.vue'),
          props: true,
        },
        // ===========================================
        // v2.10 Routes - Product Review Queue
        // ===========================================
        {
          path: 'publications/review-queue',
          name: 'product-review-queue',
          component: () => import('@/views/publications/ProductReviewQueueView.vue'),
          meta: { requiresReviewer: true },
        },
        {
          path: 'manufacturers',
          name: 'manufacturer-list',
          component: () => import('@/views/manufacturers/ManufacturerListView.vue'),
        },
        {
          path: 'manufacturers/new',
          name: 'manufacturer-create',
          component: () => import('@/views/manufacturers/ManufacturerFormView.vue'),
        },
        {
          path: 'manufacturers/:id',
          name: 'manufacturer-detail',
          component: () => import('@/views/manufacturers/ManufacturerDetailView.vue'),
          props: true,
        },
        {
          path: 'manufacturers/:id/edit',
          name: 'manufacturer-edit',
          component: () => import('@/views/manufacturers/ManufacturerFormView.vue'),
          props: true,
        },
        {
          path: 'importers',
          name: 'importer-list',
          component: () => import('@/views/importers/ImporterListView.vue'),
        },
        {
          path: 'importers/new',
          name: 'importer-create',
          component: () => import('@/views/importers/ImporterFormView.vue'),
        },
        {
          path: 'importers/:id',
          name: 'importer-detail',
          component: () => import('@/views/importers/ImporterDetailView.vue'),
          props: true,
        },
        {
          path: 'importers/:id/edit',
          name: 'importer-edit',
          component: () => import('@/views/importers/ImporterFormView.vue'),
          props: true,
        },
        {
          path: 'my-importers',
          name: 'my-importers',
          component: () => import('@/views/importers/ImporterListView.vue'),
          meta: { requiresManufacturer: true },
        },
        // v2.8: Linked manufacturers (for lab/importer companies)
        {
          path: 'linked-manufacturers',
          name: 'linked-manufacturers',
          component: () => import('@/views/partnerships/LinkedManufacturersView.vue'),
          meta: { requiresCompanyUser: true },
        },
        {
          path: 'laboratories',
          name: 'laboratory-list',
          component: () => import('@/views/laboratories/LaboratoryListView.vue'),
        },
        {
          path: 'my-laboratories',
          name: 'my-laboratories',
          component: () => import('@/views/laboratories/LaboratoryListView.vue'),
          meta: { requiresManufacturer: true },
        },
        {
          path: 'laboratories/new',
          name: 'laboratory-create',
          component: () => import('@/views/laboratories/LaboratoryFormView.vue'),
        },
        {
          path: 'laboratories/:id',
          name: 'laboratory-detail',
          component: () => import('@/views/laboratories/LaboratoryDetailView.vue'),
          props: true,
        },
        {
          path: 'laboratories/:id/edit',
          name: 'laboratory-edit',
          component: () => import('@/views/laboratories/LaboratoryFormView.vue'),
          props: true,
        },
        {
          path: 'users',
          name: 'user-list',
          component: () => import('@/views/users/UserListView.vue'),
          meta: { requiresAdmin: true },
        },
        {
          path: 'users/new',
          name: 'user-create',
          component: () => import('@/views/users/UserFormView.vue'),
          meta: { requiresAdmin: true },
        },
        {
          path: 'users/:id/edit',
          name: 'user-edit',
          component: () => import('@/views/users/UserFormView.vue'),
          props: true,
          meta: { requiresAdmin: true },
        },
        {
          path: 'audit-logs',
          name: 'audit-logs',
          component: () => import('@/views/audit/AuditLogView.vue'),
          meta: { requiresAdmin: true },
        },
        // ===========================================
        // V3 Routes - Companies, Countries, Projects
        // ===========================================
        {
          path: 'companies',
          name: 'company-list',
          component: () => import('@/views/clients/ClientListView.vue'),
        },
        {
          path: 'companies/new',
          name: 'company-create',
          component: () => import('@/views/clients/ClientFormView.vue'),
        },
        {
          path: 'companies/:id/edit',
          name: 'company-edit',
          component: () => import('@/views/clients/ClientFormView.vue'),
          props: true,
        },
        {
          path: 'companies/:id/preferences',
          name: 'company-preferences',
          component: () => import('@/views/clients/ClientPreferencesView.vue'),
          props: true,
          meta: { requiresAdmin: true },  // v2.4: US-3.2
        },
        {
          path: 'invitations',
          name: 'invitations',
          component: () => import('@/views/invitations/InvitationListView.vue'),
          meta: { requiresAdmin: true },  // v2.4: US-3.3, US-3.4, CL-003 (ORBIS Admin only)
        },
        {
          path: 'publications/scheduled',
          name: 'scheduled-publications',
          component: () => import('@/views/publications/ScheduledPublicationsView.vue'),
          meta: { requiresAdmin: true },  // v2.4: US-8.3 (Publication Scheduling)
        },
        // v2.4: Regulator System Routes (US-5.2, Phase 4)
        {
          path: 'regulators/authorities',
          name: 'regulator-authorities',
          component: () => import('@/views/regulators/RegulatorAuthorityListView.vue'),
          meta: { requiresAdmin: true },  // CL-037: ORBIS Admin only
        },
        {
          path: 'regulators/grants',
          name: 'regulator-grants',
          component: () => import('@/views/regulators/RegulatorGrantManagerView.vue'),
          meta: { requiresAdmin: true },  // CL-045: ORBIS Admin (Client Admin for own docs - future)
        },
        {
          path: 'regulators/users',
          name: 'regulator-users',
          component: () => import('@/views/regulators/RegulatorUserManagementView.vue'),
          meta: { requiresAdmin: true },  // CL-037: ORBIS Admin only
        },
        {
          path: 'regulators/comments',
          name: 'regulator-comments',
          component: () => import('@/views/regulators/RegulatorCommentsView.vue'),
          meta: { requiresAdmin: true },  // CL-041: ORBIS Admin (Client for own docs)
        },
        // v2.8: Country-to-Authority Allowlist (CL-074, US-5.5)
        {
          path: 'regulators/country-allowlist',
          name: 'regulator-country-allowlist',
          component: () => import('@/views/regulators/CountryAllowlistView.vue'),
          meta: { requiresAdmin: true },  // CL-074: ORBIS Admin only
        },
        {
          path: 'countries',
          name: 'country-list',
          component: () => import('@/views/countries/CountryListView.vue'),
        },
        {
          path: 'countries/new',
          name: 'country-create',
          component: () => import('@/views/countries/CountryFormView.vue'),
          meta: { requiresAdmin: true },
        },
        {
          path: 'countries/:id/edit',
          name: 'country-edit',
          component: () => import('@/views/countries/CountryFormView.vue'),
          props: true,
          meta: { requiresAdmin: true },
        },
        {
          path: 'projects',
          name: 'project-list',
          component: () => import('@/views/projects/ProjectListView.vue'),
        },
        {
          path: 'projects/new',
          name: 'project-create',
          component: () => import('@/views/projects/ProjectFormView.vue'),
        },
        // v2.11: Country-specific projects
        {
          path: 'projects/:id',
          name: 'project-detail',
          component: () => import('@/views/projects/ProjectDetailView_v211.vue'),
          props: true,
        },
        {
          path: 'admin/projects/review-queue',
          name: 'project-review-queue',
          component: () => import('@/views/projects/ProjectReviewQueueView.vue'),
          meta: { requiresReviewer: true },  // v2.11: ORBIS Staff only
        },
        {
          path: 'projects/:id/edit',
          name: 'project-edit',
          component: () => import('@/views/projects/ProjectFormView.vue'),
          props: true,
        },
      ],
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/views/NotFoundView.vue'),
    },
  ],
});

// Navigation guard
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();

  // Check authentication on first load
  if (!authStore.isAuthenticated && localStorage.getItem('access_token')) {
    await authStore.checkAuth();
  }

  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next({ name: 'login', query: { redirect: to.fullPath } });
  } else if (to.meta.guest && authStore.isAuthenticated) {
    next({ name: 'dashboard' });
  } else if (to.meta.requiresAdmin && !authStore.isAdmin) {
    next({ name: 'dashboard' });
  } else if (to.meta.requiresReviewer) {
    // Reviewer routes accessible by ADMIN and REVIEWER roles
    const role = authStore.user?.role;
    if (role !== 'ADMIN' && role !== 'REVIEWER') {
      next({ name: 'dashboard' });
    } else {
      next();
    }
  } else if (to.meta.requiresManufacturer && !authStore.isCompanyAdmin && !authStore.isManufacturer) {
    // Manufacturer routes accessible by Company Admins or legacy Manufacturer users
    next({ name: 'dashboard' });
  } else if (to.meta.requiresCompanyAdmin && !authStore.isCompanyAdmin && !authStore.isAdmin) {
    // Company Admin routes only accessible by Company Admins or ORBIS Admin
    next({ name: 'dashboard' });
  } else if (to.meta.requiresCompanyUser && !authStore.isCompanyUser && !authStore.isAdmin) {
    // Company User routes accessible by any company user or ORBIS Admin
    next({ name: 'dashboard' });
  } else {
    next();
  }
});

export default router;

